"use client";

import Lottie from "lottie-react";
import { useEffect, useMemo, useState } from "react";
import addToListAnimationData from "./icons/add-to-list-icon.json";
import creditAnimationData from "./icons/credit-icon.json";
import discountAnimationData from "./icons/discount-icon.json";
import pointsAnimationData from "./icons/points-icon.json";

export type LottieIconName =
  | "points-icon"
  | "discount-icon"
  | "credit-icon"
  | "add-to-list-icon";

export type LottieIconColor =
  | "primary"
  | "accent"
  | "jets-black"
  | "jets-beige"
  | "success"
  | "warning"
  | "info";

export interface LottieIconProps {
  iconName: LottieIconName;
  color: LottieIconColor;
  loop?: boolean;
  autoPlay?: boolean;
}

export function LottieIcon({
  iconName,
  color,
  loop = true,
  autoPlay = true,
}: LottieIconProps) {
  const [colorValue, setColorValue] = useState<string>("");
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(`--color-${color}`)
      .trim();
    setColorValue(value);
  }, [color]);

  const animationData = useMemo(() => {
    if (!isMounted || !colorValue) return null;
    const data = getAnimationData(iconName);
    return updateLottieColors(data, colorValue);
  }, [iconName, colorValue, isMounted]);

  if (!animationData) return null;

  return (
    <Lottie animationData={animationData} loop={loop} autoPlay={autoPlay} />
  );
}

function getAnimationData(iconName: LottieIconName) {
  switch (iconName) {
    case "points-icon":
      return pointsAnimationData;
    case "credit-icon":
      return creditAnimationData;
    case "discount-icon":
      return discountAnimationData;
    case "add-to-list-icon":
      return addToListAnimationData;
  }
}

// Spec says fourth color is optional, but our reference data has it
type Color = [number, number, number, number];

interface SingleFrameColor {
  a: 0;
  k: Color;
}

// This doesn't occur in the icons we use, but it's in the spec
interface MultipleFrameColor {
  a: 1;
  k: Color[];
}

type AnimatedColor = SingleFrameColor | MultipleFrameColor;

interface StrokeShape {
  ty: "st";
  c: AnimatedColor;
}

interface FillShape {
  ty: "fl";
  c: AnimatedColor;
}

function isColoredShape(s: Shape): s is StrokeShape | FillShape {
  return s.ty === "st" || s.ty === "fl";
}

interface GroupShape {
  ty: "gr";
  it: Shape[];
}

type UnknownShape = object & {
  ty: string;
};

type Shape = StrokeShape | GroupShape | UnknownShape;

interface Layer {
  shapes?: Shape[];
}

interface LottieIconObject {
  layers: Layer[];
  assets: { layers: Layer[] }[];
}

function isGroupShape(s: Shape): s is GroupShape {
  return s.ty === "gr";
}

// This was the color selected when the icon was exported.
const primaryIconColor = [0, 0, 0, 1] as const;

type PrimaryColorFloatRgb = readonly [number, number, number];

function updateLottieColors(
  iconJson: LottieIconObject,
  colorHex: string,
): LottieIconObject {
  const primaryColor = colorHex.match(
    /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i,
  );
  if (!primaryColor) {
    return iconJson;
  }
  const primaryColorFloatRgb: PrimaryColorFloatRgb = [
    parseInt(primaryColor[1], 16) / 255,
    parseInt(primaryColor[2], 16) / 255,
    parseInt(primaryColor[3], 16) / 255,
  ];

  return {
    ...iconJson,
    assets: iconJson.assets.map((asset) => {
      return {
        ...asset,
        layers: mapLayers(asset.layers, primaryColorFloatRgb),
      };
    }),
    layers: mapLayers(iconJson.layers, primaryColorFloatRgb),
  };
}

function mapLayers(
  layers: Layer[],
  primaryColorFloatRgb: PrimaryColorFloatRgb,
) {
  return layers.map((layer) => {
    if (!layer.shapes) {
      return layer;
    }
    return {
      ...layer,
      shapes: mapShapes(layer.shapes, primaryColorFloatRgb),
    };
  });
}

function mapShapes(
  shapes: Shape[],
  primaryColorFloatRgb: PrimaryColorFloatRgb,
): Shape[] {
  return shapes.map((shape) => {
    if (isGroupShape(shape)) {
      return {
        ...shape,
        it: mapShapes(shape.it, primaryColorFloatRgb),
      };
    }
    if (!isColoredShape(shape)) {
      return shape;
    }
    const c = shape.c;
    if (c.a === 0 && c.k.every((v, i) => primaryIconColor[i] === v)) {
      return {
        ...shape,
        c: {
          ...c,
          k: [...primaryColorFloatRgb, 1],
        },
      };
    }
    return shape;
  });
}
